import { useState, useRef } from 'react';
import axios from 'axios';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { ArrowLeft, ArrowRight } from 'lucide-react';

import StepProgress from './StepProgress';
import StepPersonal from './StepPersonal';
import StepEducation from './StepEducation';
import StepDocuments from './StepDocuments';
import StepReview from './StepReview';
import SuccessScreen from './SuccessScreen';
import Button from '../Button';

import type {
  ScholarshipFormData,
  ScholarshipEducationLevel,
  ScholarshipDocumentSlot,
  ScholarshipDocumentsState,
  FormErrors,
  ScholarshipSubmitPayload,
  ScholarshipSubmitResponse,
} from './types';
import {
  validateStep1,
  validateStep2,
  validateField,
  cleanPhone,
  normalizeArabicDigits,
  extractErrorMessage,
} from './validation';
import scholarshipGrantService from '../../services/scholarshipGrant.service';

const INITIAL_FORM_DATA: ScholarshipFormData = {
  full_name: '',
  email: '',
  phone: '',
  date_of_birth: '',
  governorate: '',
  education_level: '',
  school_name: '',
  university_name: '',
  faculty: '',
  major: '',
  graduation_year: '',
  academic_grade: '',
  preferred_study_field: '',
  motivation: '',
  achievements: '',
};

const INITIAL_DOCUMENTS_STATE: ScholarshipDocumentsState = {
  academic_certificate: { status: 'empty' },
  national_id_front: { status: 'empty' },
  national_id_back: { status: 'empty' },
};

export default function ScholarshipForm() {
  const [step, setStep] = useState<number>(1);
  const [formData, setFormData] = useState<ScholarshipFormData>(INITIAL_FORM_DATA);
  const [errors, setErrors] = useState<FormErrors>({});
  const [documents, setDocuments] = useState<ScholarshipDocumentsState>(INITIAL_DOCUMENTS_STATE);

  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitResult, setSubmitResult] = useState<ScholarshipSubmitResponse | null>(null);

  const isSubmittingRef = useRef<boolean>(false);
  const draftTokenRef = useRef<string | null>(null);
  const draftPromiseRef = useRef<Promise<string> | null>(null);

  const shouldReduceMotion = useReducedMotion();

  const getOrCreateDraftToken = async (): Promise<string> => {
    if (draftTokenRef.current) return draftTokenRef.current;
    if (draftPromiseRef.current) return draftPromiseRef.current;

    const p = (async () => {
      try {
        const res = await scholarshipGrantService.createDraft();
        draftTokenRef.current = res.draft_token;
        return res.draft_token;
      } finally {
        draftPromiseRef.current = null;
      }
    })();

    draftPromiseRef.current = p;
    return p;
  };

  const handleChange = (name: keyof ScholarshipFormData, value: string) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      const err = validateField(name, value, { ...formData, [name]: value });
      setErrors((prev) => ({ ...prev, [name]: err }));
    }
  };

  const handleBlur = (name: keyof ScholarshipFormData) => {
    const err = validateField(name, formData[name], formData);
    setErrors((prev) => ({ ...prev, [name]: err }));
  };

  const handleEducationLevelChange = (level: ScholarshipEducationLevel) => {
    setFormData((prev) => {
      const isSchool = level === 'high_school_student' || level === 'high_school_graduate';
      const isUni = level === 'university_student' || level === 'university_graduate';
      return {
        ...prev,
        education_level: level,
        school_name: isSchool ? prev.school_name : '',
        university_name: isUni ? prev.university_name : '',
        faculty: isUni ? prev.faculty : '',
        major: isUni ? prev.major : '',
      };
    });

    setErrors((prev) => {
      const next = { ...prev };
      delete next.education_level;
      const isSchool = level === 'high_school_student' || level === 'high_school_graduate';
      const isUni = level === 'university_student' || level === 'university_graduate';
      if (!isSchool) delete next.school_name;
      if (!isUni) {
        delete next.university_name;
        delete next.faculty;
        delete next.major;
      }
      return next;
    });
  };

  const handleSelectFile = async (slot: ScholarshipDocumentSlot, file: File) => {
    setDocuments((prev) => ({
      ...prev,
      [slot]: { status: 'uploading', fileName: file.name, fileSize: file.size, progress: 0, file },
    }));

    try {
      const token = await getOrCreateDraftToken();
      const res = await scholarshipGrantService.uploadDocument(token, slot, file, (e) => {
        const total = e.total || file.size;
        const pct = Math.round((e.loaded * 100) / total);
        setDocuments((prev) => ({
          ...prev,
          [slot]: { ...prev[slot], progress: Math.min(pct, 100) },
        }));
      });

      setDocuments((prev) => ({
        ...prev,
        [slot]: { status: 'success', fileName: res.file_name, fileSize: res.file_size, progress: 100 },
      }));
    } catch (err: unknown) {
      let msg = 'الملف مارفعش. جرب تاني.';
      if (axios.isAxiosError(err)) {
        if (err.response?.status === 429) msg = 'محاولات كتير أوي. استنى شوية وحاول تاني.';
        else if (!err.response) msg = 'تأكد من اتصالك بالإنترنت وحاول تاني.';
        else msg = extractErrorMessage(err.response.data?.detail, msg);
      }
      setDocuments((prev) => ({
        ...prev,
        [slot]: { status: 'error', fileName: file.name, fileSize: file.size, errorMessage: msg },
      }));
    }
  };

  const handleRemoveDocument = async (slot: ScholarshipDocumentSlot) => {
    const token = draftTokenRef.current;
    if (token) {
      try {
        await scholarshipGrantService.removeDocument(token, slot);
      } catch {
        // Reset slot locally
      }
    }
    setDocuments((prev) => ({ ...prev, [slot]: { status: 'empty' } }));
  };

  const handleClientError = (slot: ScholarshipDocumentSlot, error: string, file: File) => {
    setDocuments((prev) => ({
      ...prev,
      [slot]: { status: 'error', fileName: file.name, fileSize: file.size, errorMessage: error },
    }));
  };

  const isAnyUploadInProgress = Object.values(documents).some((d) => d.status === 'uploading');

  const isStep1Valid = Object.keys(validateStep1(formData)).length === 0;
  const isStep2Valid = Object.keys(validateStep2(formData)).length === 0;
  const isStep3Valid =
    documents.academic_certificate.status === 'success' &&
    documents.national_id_front.status === 'success' &&
    documents.national_id_back.status === 'success';

  const isCurrentStepValid =
    step === 1 ? isStep1Valid : step === 2 ? isStep2Valid : step === 3 ? isStep3Valid : true;

  const focusFirstInvalidField = (stepErrors: FormErrors) => {
    const firstField = Object.keys(stepErrors)[0];
    if (!firstField) return;
    const el = document.querySelector(`[name="${firstField}"]`) as HTMLElement | null;
    if (el) {
      el.scrollIntoView({
        behavior: shouldReduceMotion ? 'auto' : 'smooth',
        block: 'center',
      });
      el.focus({ preventScroll: true });
    }
  };

  const handleNext = () => {
    if (step === 1) {
      const step1Errors = validateStep1(formData);
      if (Object.keys(step1Errors).length > 0) {
        setErrors((prev) => ({ ...prev, ...step1Errors }));
        focusFirstInvalidField(step1Errors);
        return;
      }
    } else if (step === 2) {
      const step2Errors = validateStep2(formData);
      if (Object.keys(step2Errors).length > 0) {
        setErrors((prev) => ({ ...prev, ...step2Errors }));
        focusFirstInvalidField(step2Errors);
        return;
      }
    }
    setStep((prev) => Math.min(prev + 1, 4));
    window.scrollTo({ top: document.getElementById('apply')?.offsetTop || 0, behavior: 'smooth' });
  };

  const handlePrev = () => {
    setStep((prev) => Math.max(prev - 1, 1));
  };

  const handleSubmit = async () => {
    if (isSubmittingRef.current) return;
    isSubmittingRef.current = true;
    setIsSubmitting(true);
    setSubmitError(null);

    try {
      const token = await getOrCreateDraftToken();
      const isSchool =
        formData.education_level === 'high_school_student' ||
        formData.education_level === 'high_school_graduate';
      const isUni =
        formData.education_level === 'university_student' ||
        formData.education_level === 'university_graduate';

      const payload: ScholarshipSubmitPayload = {
        draft_token: token,
        full_name: formData.full_name.trim(),
        email: formData.email.trim().toLowerCase(),
        phone: cleanPhone(formData.phone),
        date_of_birth: formData.date_of_birth.trim(),
        governorate: formData.governorate,
        education_level: formData.education_level as ScholarshipEducationLevel,
        school_name: isSchool ? formData.school_name.trim() : null,
        university_name: isUni ? formData.university_name.trim() : null,
        faculty: isUni ? formData.faculty.trim() : null,
        major: isUni ? (formData.major.trim() || null) : null,
        graduation_year: parseInt(normalizeArabicDigits(formData.graduation_year), 10),
        academic_grade: formData.academic_grade.trim(),
        preferred_study_field: formData.preferred_study_field.trim(),
        motivation: formData.motivation.trim(),
        achievements: formData.achievements.trim() || null,
      };

      const res = await scholarshipGrantService.submit(payload);
      setSubmitResult(res);
    } catch (err: unknown) {
      let msg = 'حصلت مشكلة أثناء إرسال الطلب. حاول تاني بعد شوية.';
      if (axios.isAxiosError(err)) {
        if (err.response?.status === 429) msg = 'محاولات كتير أوي. استنى شوية وحاول تاني.';
        else if (!err.response) msg = 'تأكد من اتصالك بالإنترنت وحاول تاني.';
        else msg = extractErrorMessage(err.response.data?.detail, msg);
      }
      setSubmitError(msg);
    } finally {
      isSubmittingRef.current = false;
      setIsSubmitting(false);
    }
  };

  if (submitResult) {
    return (
      <SuccessScreen
        referenceNumber={submitResult.reference_number}
        isDuplicate={submitResult.duplicate}
      />
    );
  }

  const anim = shouldReduceMotion
    ? { initial: { opacity: 1 }, animate: { opacity: 1 }, exit: { opacity: 1 } }
    : {
        initial: { opacity: 0, y: 10 },
        animate: { opacity: 1, y: 0, transition: { duration: 0.18 } },
        exit: { opacity: 0, y: -10, transition: { duration: 0.18 } },
      };

  return (
    <div className="rounded-3xl border-2 border-gray-200 bg-white p-6 sm:p-8 shadow-xs">
      <StepProgress currentStep={step} />

      <AnimatePresence mode="wait">
        <motion.div key={step} {...anim}>
          {step === 1 && (
            <StepPersonal
              values={formData}
              errors={errors}
              onChange={handleChange}
              onBlur={handleBlur}
            />
          )}
          {step === 2 && (
            <StepEducation
              values={formData}
              errors={errors}
              onChange={handleChange}
              onEducationLevelChange={handleEducationLevelChange}
              onBlur={handleBlur}
            />
          )}
          {step === 3 && (
            <StepDocuments
              documents={documents}
              onSelectFile={handleSelectFile}
              onRemove={handleRemoveDocument}
              onClientError={handleClientError}
              isAnyUploadInProgress={isAnyUploadInProgress}
            />
          )}
          {step === 4 && (
            <StepReview
              values={formData}
              documents={documents}
              onEdit={(s) => setStep(s)}
              onSubmit={handleSubmit}
              isSubmitting={isSubmitting}
              submitError={submitError}
            />
          )}
        </motion.div>
      </AnimatePresence>

      {/* Navigation action row for steps 1-3 (and back on step 4) */}
      {step < 4 && (
        <div className="flex items-center justify-between mt-8 pt-6 border-t border-gray-100">
          {step > 1 ? (
            <Button
              type="button"
              variant="secondary"
              onClick={handlePrev}
              className="gap-2 px-5 py-2.5 text-sm"
            >
              <ArrowRight className="w-4 h-4" />
              <span>رجوع</span>
            </Button>
          ) : (
            <div />
          )}

          <Button
            type="button"
            variant="primary"
            onClick={handleNext}
            disabled={!isCurrentStepValid || isAnyUploadInProgress}
            className="gap-2 px-6 py-2.5 text-sm font-semibold disabled:opacity-50"
          >
            <span>التالي</span>
            <ArrowLeft className="w-4 h-4" />
          </Button>
        </div>
      )}
    </div>
  );
}
